import type {
  PdfConfirmAction,
  PdfConfirmFieldName,
  PdfConfirmFieldValue,
  PdfConfirmFields,
  PdfConfirmRequest,
  PdfPreviewEdits,
  PdfPreviewSnapshot,
  PdfPreviewUnit,
} from '../../types/pdfPreview';
import { PDF_SHARED_FIELDS, PDF_UNIT_FIELDS } from '../../types/pdfPreview';
import { validPdfConfirmField } from './pdfConfirmFields';

const record = (value: unknown): value is Record<string, unknown> =>
  value !== null && typeof value === 'object' && !Array.isArray(value);
const exactAmount = (value: unknown): value is string =>
  typeof value === 'string' && /^(0|[1-9]\d{0,9})$/.test(value) && Number(value) <= 2147483647;

export const previewUnits = (snapshot: PdfPreviewSnapshot) => [
  ...snapshot.projects.flatMap((project) =>
    project.units.map((unit) => ({ unit, projectKey: project.projectKey }))
  ),
  ...snapshot.ungroupedUnits.map((unit) => ({ unit, projectKey: null })),
];

export const createPdfPreviewEdits = (snapshot: PdfPreviewSnapshot): PdfPreviewEdits => {
  const selectable = new Set(
    (snapshot.media ?? [])
      .filter(
        (media) =>
          media.status === 'prepared' && media.kind === 'property_photo' && !media.needsReview
      )
      .map((media) => media.mediaId)
  );
  return {
    projectOverrides: {},
    actions: {},
    acknowledgePartial: false,
    units: Object.fromEntries(
      previewUnits(snapshot).map(({ unit }) => [
        unit.unitKey,
        {
          fields: {},
          selectedMediaIds: Array.from(
            new Set(
              (unit.recommendedMediaIds ?? []).filter(
                (id) => selectable.has(id) && !unit.excludedMediaIds?.includes(id)
              )
            )
          ).slice(0, 50),
        },
      ])
    ),
  };
};

const sameValue = (left: PdfConfirmFieldValue, right: PdfConfirmFieldValue) =>
  Array.isArray(left) && Array.isArray(right)
    ? left.length === right.length && left.every((value, index) => value === right[index])
    : left === right;

export const isPdfPreviewDirty = (
  snapshot: PdfPreviewSnapshot,
  edits: PdfPreviewEdits
): boolean => {
  if (Object.keys(edits.actions).length > 0 || edits.acknowledgePartial) return true;
  for (const project of snapshot.projects) {
    const overrides = edits.projectOverrides[project.projectKey];
    for (const field of PDF_SHARED_FIELDS) {
      if (
        overrides &&
        Object.prototype.hasOwnProperty.call(overrides, field) &&
        !sameValue(overrides[field] ?? null, originalConfirmValue(project.sharedProject, field))
      )
        return true;
    }
  }

  const initial = createPdfPreviewEdits(snapshot);
  for (const { unit } of previewUnits(snapshot)) {
    const current = edits.units[unit.unitKey];
    if (!current) continue;
    for (const field of PDF_UNIT_FIELDS) {
      if (
        Object.prototype.hasOwnProperty.call(current.fields, field) &&
        !sameValue(current.fields[field] ?? null, originalConfirmValue(unit, field))
      )
        return true;
    }
    const originalMedia = initial.units[unit.unitKey].selectedMediaIds;
    if (
      current.selectedMediaIds.length !== originalMedia.length ||
      current.selectedMediaIds.some((mediaId, index) => mediaId !== originalMedia[index])
    )
      return true;
  }
  return false;
};

export const setPdfConfirmAction = (
  edits: PdfPreviewEdits,
  unitKey: string,
  action: PdfConfirmAction | null
): PdfPreviewEdits => {
  if (!Object.prototype.hasOwnProperty.call(edits.units, unitKey)) return edits;
  const actions = { ...edits.actions };
  if (action === null) delete actions[unitKey];
  else actions[unitKey] = action;
  return { ...edits, actions };
};

export const setPdfPartialAcknowledgment = (
  edits: PdfPreviewEdits,
  acknowledgePartial: boolean
): PdfPreviewEdits => ({ ...edits, acknowledgePartial });

type ConfirmBuild =
  | { ok: true; request: PdfConfirmRequest }
  | { ok: false; error: string; unitKey?: string; field?: PdfConfirmFieldName };

const confirmFields = (
  overrides: PdfConfirmFields | undefined,
  source: Pick<PdfPreviewUnit, 'fields' | 'location'>,
  allowed: readonly PdfConfirmFieldName[],
  unitKey?: string
): { ok: true; fields: PdfConfirmFields } | Exclude<ConfirmBuild, { ok: true }> => {
  const result: PdfConfirmFields = {};
  for (const [key, value] of Object.entries(overrides ?? {})) {
    const field = key as PdfConfirmFieldName;
    if (!allowed.includes(field) || !validPdfConfirmField(field, value))
      return { ok: false, error: `Review ${field} before confirming.`, unitKey, field };
    if (!sameValue(value, originalConfirmValue(source, field))) result[field] = value;
  }
  return { ok: true, fields: result };
};

export const buildPdfConfirmRequest = (
  snapshot: PdfPreviewSnapshot,
  edits: PdfPreviewEdits,
  replacement?: { existingPropertyAdId: string; expectedVersion: number }
): ConfirmBuild => {
  if (
    !Number.isFinite(Date.parse(snapshot.expiresAt)) ||
    Date.parse(snapshot.expiresAt) <= Date.now()
  )
    return { ok: false, error: 'This preview has expired. Confirmation is unavailable.' };
  const units = previewUnits(snapshot);
  if (units.length > 32 || new Set(units.map(({ unit }) => unit.unitKey)).size !== units.length)
    return { ok: false, error: 'The preview contains an invalid listing set.' };
  if (units.some(({ unit }) => !/^u_[a-f0-9]{32}$/.test(unit.unitKey)))
    return { ok: false, error: 'The preview contains an invalid listing identifier.' };
  if (
    Object.keys(edits.actions).length !== units.length ||
    units.some(({ unit }) => !edits.actions[unit.unitKey])
  )
    return { ok: false, error: 'Choose Editor, Draft or Skip for every listing.' };
  const editorCount = units.filter(({ unit }) => edits.actions[unit.unitKey] === 'editor').length;
  if (editorCount > 1) return { ok: false, error: 'Only one listing can open in the Editor.' };
  if (replacement && editorCount !== 1)
    return { ok: false, error: 'Replacement consent requires an Editor listing.' };
  if (
    (snapshot.processingStatus === 'partial' || snapshot.requiresPartialAcknowledgment) &&
    !edits.acknowledgePartial
  )
    return { ok: false, error: 'Acknowledge the incomplete PDF analysis before confirming.' };

  const projectOverrides: PdfConfirmRequest['projectOverrides'] = [];
  for (const project of snapshot.projects) {
    const checked = confirmFields(
      edits.projectOverrides[project.projectKey],
      project.sharedProject,
      PDF_SHARED_FIELDS
    );
    if (!checked.ok) return checked;
    if (Object.keys(checked.fields).length > 0) {
      if (!/^p_[a-f0-9]{32}$/.test(project.projectKey))
        return { ok: false, error: 'The preview contains an invalid project identifier.' };
      projectOverrides.push({ projectKey: project.projectKey, fields: checked.fields });
    }
  }
  if (projectOverrides.length > 6)
    return { ok: false, error: 'Too many project overrides for one confirmation.' };

  const preparedMedia = new Set(
    (snapshot.media ?? [])
      .filter((media) => media.status === 'prepared')
      .map((media) => media.mediaId)
  );
  const decisions: PdfConfirmRequest['decisions'] = [];
  for (const { unit } of units) {
    const action = edits.actions[unit.unitKey];
    if (action === 'skip') {
      decisions.push({ unitKey: unit.unitKey, action: 'skip' });
      continue;
    }
    if (action !== 'editor' && action !== 'draft')
      return { ok: false, error: 'Choose an action for this listing.', unitKey: unit.unitKey };
    const current = edits.units[unit.unitKey];
    if (!current)
      return { ok: false, error: 'Listing edits are unavailable.', unitKey: unit.unitKey };
    const checked = confirmFields(current.fields, unit, PDF_UNIT_FIELDS, unit.unitKey);
    if (!checked.ok) return checked;
    const mediaIds = current.selectedMediaIds;
    if (
      mediaIds.length > 50 ||
      new Set(mediaIds).size !== mediaIds.length ||
      mediaIds.some(
        (id) =>
          !/^m_[a-f0-9]{32}$/.test(id) ||
          !preparedMedia.has(id) ||
          unit.excludedMediaIds?.includes(id)
      )
    )
      return {
        ok: false,
        error: 'Review selected photos for this listing.',
        unitKey: unit.unitKey,
      };
    decisions.push({
      unitKey: unit.unitKey,
      action,
      fields: checked.fields,
      selectedMediaIds: [...mediaIds],
    });
  }

  return {
    ok: true,
    request: {
      projectOverrides,
      decisions,
      acknowledgePartial: edits.acknowledgePartial,
      ...(replacement ? { replaceExistingInProgress: { approved: true, ...replacement } } : {}),
    },
  };
};

export const setProjectOverride = (
  edits: PdfPreviewEdits,
  projectKey: string,
  field: PdfConfirmFieldName,
  value: PdfConfirmFieldValue
): PdfPreviewEdits =>
  (PDF_SHARED_FIELDS as readonly string[]).includes(field)
    ? {
        ...edits,
        projectOverrides: {
          ...edits.projectOverrides,
          [projectKey]: { ...edits.projectOverrides[projectKey], [field]: value },
        },
      }
    : edits;

export const setUnitOverride = (
  edits: PdfPreviewEdits,
  unitKey: string,
  field: PdfConfirmFieldName,
  value: PdfConfirmFieldValue
): PdfPreviewEdits =>
  (PDF_UNIT_FIELDS as readonly string[]).includes(field)
    ? {
        ...edits,
        units: {
          ...edits.units,
          [unitKey]: {
            fields: { ...edits.units[unitKey]?.fields, [field]: value },
            selectedMediaIds: edits.units[unitKey]?.selectedMediaIds ?? [],
          },
        },
      }
    : edits;

export const toggleUnitMedia = (
  snapshot: PdfPreviewSnapshot,
  edits: PdfPreviewEdits,
  unitKey: string,
  mediaId: string
): PdfPreviewEdits => {
  const unit = previewUnits(snapshot).find((entry) => entry.unit.unitKey === unitKey)?.unit;
  if (!unit || unit.excludedMediaIds?.includes(mediaId)) return edits;
  if (
    !(snapshot.media ?? []).some(
      (media) => media.mediaId === mediaId && media.status === 'prepared'
    )
  )
    return edits;
  const current = edits.units[unitKey];
  if (!current) return edits;
  const selected = current.selectedMediaIds.includes(mediaId)
    ? current.selectedMediaIds.filter((id) => id !== mediaId)
    : current.selectedMediaIds.length < 50
      ? [...current.selectedMediaIds, mediaId]
      : current.selectedMediaIds;
  return {
    ...edits,
    units: { ...edits.units, [unitKey]: { ...current, selectedMediaIds: selected } },
  };
};

export const moveUnitMedia = (
  edits: PdfPreviewEdits,
  unitKey: string,
  mediaId: string,
  direction: -1 | 1
): PdfPreviewEdits => {
  const current = edits.units[unitKey];
  if (!current) return edits;
  const index = current.selectedMediaIds.indexOf(mediaId);
  const nextIndex = index + direction;
  if (index < 0 || nextIndex < 0 || nextIndex >= current.selectedMediaIds.length) return edits;
  const selectedMediaIds = [...current.selectedMediaIds];
  [selectedMediaIds[index], selectedMediaIds[nextIndex]] = [
    selectedMediaIds[nextIndex],
    selectedMediaIds[index],
  ];
  return {
    ...edits,
    units: { ...edits.units, [unitKey]: { ...current, selectedMediaIds } },
  };
};

const confirmedValue = (unit: Pick<PdfPreviewUnit, 'fields'>, field: string): unknown =>
  unit.fields[field]?.state === 'confirmed' ? unit.fields[field].value : null;

export const originalConfirmValue = (
  unit: Pick<PdfPreviewUnit, 'fields' | 'location'>,
  field: PdfConfirmFieldName
): PdfConfirmFieldValue => {
  if (field === 'locationId')
    return unit.location.status === 'matched' ? unit.location.locationId : null;
  const price = confirmedValue(unit, 'price');
  const exactPrice = record(price) && price.qualifier === 'exact' && exactAmount(price.amount);
  if (field === 'currency' && exactPrice && typeof price.currency === 'string')
    return price.currency;
  if (field === 'defaultRentFrequency') {
    const rentalPeriod = confirmedValue(unit, 'rentalPeriod');
    if (typeof rentalPeriod === 'string') return rentalPeriod;
    return exactPrice &&
      confirmedValue(unit, 'activityKind') === 'rent' &&
      typeof price.period === 'string'
      ? price.period
      : null;
  }
  if (field === 'builtUpArea' || field === 'plotArea') {
    const area = confirmedValue(unit, field);
    const sqft = record(area) && area.qualifier === 'exact' ? area.sqft : null;
    return exactAmount(sqft) && Number(sqft) > 0 ? Number(sqft) : null;
  }
  if (['price', 'priceDaily', 'priceWeekly', 'priceMonthly', 'priceYearly'].includes(field)) {
    if (!exactPrice) return null;
    const activity = confirmedValue(unit, 'activityKind');
    const period = price.period;
    const key =
      activity === 'rent' &&
      typeof period === 'string' &&
      ['yearly', 'monthly', 'weekly', 'daily'].includes(period)
        ? `price${period.charAt(0).toUpperCase()}${period.slice(1)}`
        : activity === 'sale'
          ? 'price'
          : null;
    return field === key ? String(price.amount) : null;
  }
  const value = confirmedValue(unit, field);
  if (field === 'handoverYear' && typeof value === 'number') return String(value);
  if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean')
    return value;
  if (Array.isArray(value) && value.every((item) => typeof item === 'string')) return value;
  return null;
};

export const effectiveConfirmValue = (
  unit: PdfPreviewUnit,
  field: PdfConfirmFieldName,
  edits: PdfPreviewEdits,
  projectKey: string | null,
  sharedOriginalTitle?: PdfConfirmFieldValue
): PdfConfirmFieldValue => {
  const unitFields = edits.units[unit.unitKey]?.fields;
  if (unitFields && Object.prototype.hasOwnProperty.call(unitFields, field))
    return unitFields[field] ?? null;
  const projectFields = projectKey ? edits.projectOverrides[projectKey] : undefined;
  if (field === 'title' && projectFields?.title !== undefined) {
    const originalTitle = originalConfirmValue(unit, 'title');
    if (originalTitle !== null && originalTitle !== sharedOriginalTitle) return originalTitle;
  }
  if (projectFields && Object.prototype.hasOwnProperty.call(projectFields, field))
    return projectFields[field] ?? null;
  return originalConfirmValue(unit, field);
};
